import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminPlans } from "@/services/adminService";
import type { AdminPlansBundle } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import PlansTab from "@/pages/portal/admin/sections/plans/PlansTab";
import ComparisonTab from "@/pages/portal/admin/sections/plans/ComparisonTab";
import FaqTab from "@/pages/portal/admin/sections/plans/FaqTab";

type TabKey = "plans" | "comparison" | "faqs";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "plans", label: "Plans", icon: "ri-price-tag-3-line" },
  { key: "comparison", label: "Comparison table", icon: "ri-table-line" },
  { key: "faqs", label: "FAQs", icon: "ri-question-line" },
];

const EMPTY_BUNDLE: AdminPlansBundle = { plans: [], features: [], faqs: [] };

/**
 * Admin — Listing plans manager.
 *
 * Everything that drives the public "Listing plans" page can be edited here
 * without touching code: the plans themselves, the comparison table rows and
 * the FAQ accordion. Changes are reflected on /listing-plans immediately.
 */
export default function PlansSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminPlans(accessToken),
    EMPTY_BUNDLE,
    [accessToken],
  );
  const [tab, setTab] = useState<TabKey>("plans");

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                tab === t.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <i className={`${t.icon} text-sm`} />
              {t.label}
            </button>
          ))}
        </div>
        <span className="ml-auto hidden text-xs text-foreground-500 sm:block">
          Live on{" "}
          <a
            href="/listing-plans"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-700 hover:underline"
          >
            /listing-plans
          </a>
        </span>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      {tab === "plans" && (
        <PlansTab token={accessToken} data={data} setData={setData} loading={loading} />
      )}
      {tab === "comparison" && (
        <ComparisonTab token={accessToken} data={data} setData={setData} loading={loading} />
      )}
      {tab === "faqs" && (
        <FaqTab token={accessToken} data={data} setData={setData} loading={loading} />
      )}
    </div>
  );
}